import {BaseElement, BASIS, OSkinableInit} from "/@back@/commons/basis.js";
import {ActionBtn, Button, ButtonActions, OActionBtnInit} from "/@back@/commons/widgets/buttons.js";
import {GridSmall} from "/@back@/commons/widgets/grid-tags.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {OSpaceTreeInit, SpaceTree} from "/@back@/wsp/views/spaceTree.js";
import {Action} from "/@lib@/commons/actions.js";
import {IReg, IUiEnv, REG} from "/@lib@/commons/registry.js";
import {IView, IViewsContainer, OViewVisitOptions} from "/@lib@/commons/views.js";
import {JSX} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {IShortDescCtx, ITEM} from "/@lib@/wsp/item.js";
import {JSrcFields, JSrcFieldsTree, srcUri} from "/@lib@/wsp/src.js";
import {IWspEnv, Wsp} from "/@lib@/wsp/wsp.js";
import {CreateSpace} from "/@back@/wsp/actions/srcActions.js";

/**
 * Sélecteur d'un espace
 */
export interface SpaceSelector extends BaseElement, IView {
	initialize(init: OSpaceSelectorInit): this
}

export interface OSpaceSelectorInit extends OSkinableInit {
	startSel?: srcUri
	spaceTree?: OSpaceTreeInit
	buttonLabel?: string
	selOnDblClick?: boolean

	/** Supprime le bouton de création d'un espace. */
	noCreateSpace?: boolean

	/**
	 * Permet d'afficher un message en fonction de l'espace sélectionné et d'activer/désactiver le btn de sélection.
	 * cf SpaceSelector.setMsg(msg:string, allowSelect:boolean)
	 */
	onSelChange?: (src: JSrcFieldsTree, spSel: SpaceSelector) => void
}

export class SpaceSelector extends BaseElement implements IViewsContainer, IShortDescCtx {


	/** Contexte */
	reg: IReg<IWspEnv & IUiEnv>;

	get wsp(): Wsp {return this.reg.env.wsp};

	/** Eléments UI */
	spaceTree: SpaceTree;
	msgArea: HTMLElement;
	createSpBtn?: ActionBtn<SpaceSelector>;
	selectBtn: ButtonActions<SpaceSelector>;

	get shortDescs(): JSrcFields[] {return this.spaceTree.shortDescs}

	get emitter(): SpaceSelector {return this}

	protected _initialize(init: OSpaceSelectorInit) {
		this.reg = this.findReg(init);
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg.installSkin("webzone:panel", sr);
		this._initAndInstallSkin(this.localName, init);
		const initTree = BASIS.newInit(init.spaceTree, this.reg);
		initTree.hideSearch = true;
		if (init.selOnDblClick) initTree.defaultAction = new Action<IShortDescCtx>().setExecute(() => {this.doSelect()});
		initTree.srcFilter = (data: JSrcFields) => ITEM.getSrcUriType(data.srcUri) === "space";
		this.spaceTree = sr.appendChild(new SpaceTree().initialize(initTree));
		const footer = sr.appendChild(<div id="footer"/>);
		this.msgArea = footer.appendChild(<div id="msg"/>);
		if (!init.noCreateSpace) {
			this.createSpBtn = footer.appendChild(<ActionBtn id="create" ui-context="dialog" î={{
				action: new SpSelCreateSpace(),
				actionContext: this
			} as OActionBtnInit<SpaceSelector>}/> as ActionBtn<SpaceSelector>);
		}
		this.selectBtn = footer.appendChild(<Button id="select" label={init.buttonLabel || "Sélectionner"} ui-context="dialog" class="default" onclick={this.onSelectBtn}/>) as ButtonActions<SpaceSelector>;
		footer.appendChild(<Button id="cancel" label="Annuler" ui-context="dialog" onclick={this.onCancelBtn}/>);
		if (init.onSelChange) {
			this.spaceTree.grid.addEventListener("grid-select", (ev: CustomEvent) => {
				const grid = ev.currentTarget as GridSmall;
				const row = grid.dataHolder.getRow(grid.getSelectedRow());
				this.createSpBtn?.refresh();
				init.onSelChange(row ? row.rowKey as JSrcFieldsTree : null, this);
			});
			init.onSelChange(null, this);
		} else if (this.createSpBtn) {
			this.spaceTree.grid.addEventListener("grid-select", (ev: CustomEvent) => {
				this.createSpBtn.refresh();
			});
		}
		if (init.startSel) this.spaceTree.initializedAsync.then(() => {this.spaceTree.selectSrcUri(init.startSel)});
	}

	// protected _refresh() {
	// 	//appelé une fois inséré dans l'arbre.
	// }

	setMsg(msg: string, allowSelect: boolean) {
		this.msgArea.textContent = msg;
		this.selectBtn.disabled = !allowSelect;
	}

	evalPerm(perms: string | string[], src: JSrcFieldsTree, msgNotAllowed: string, msgForbidden: string): boolean {
		if (!this.reg.hasPermission(perms, src.srcRoles, null, src.srcRi)) {
			this.setMsg(this.reg.hasSystemRights(perms, src.srcRi) ? msgNotAllowed : msgForbidden, false);
			return false;
		}
		return true;
	}

	doSelect() {
		const r = this.spaceTree.shortDescs;
		if (r.length > 0) POPUP.findPopupableParent(this).close(r[0]);
	}

	onCancelBtn(this: Button) {
		POPUP.findPopupableParent(this).close();
	}

	onSelectBtn(this: Button) {
		DOMSH.findHost<SpaceSelector>(this).doSelect();
	}

	visitViews(visitor: (view: IView) => any, options?: OViewVisitOptions): any {
		return visitor(this.spaceTree);
	}

	visitViewsAsync(visitor: (view: IView) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		return visitor(this.spaceTree);
	}
}

REG.reg.registerSkin('wsp-space-selector', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 30em;
		min-width: 15em;
		flex-direction: column;
	}

	wsp-space-tree {
		border-bottom: 1px solid var(--border-color);
	}

	#footer {
		display: flex;
		min-height: 0;
		min-width: 0;
		align-items: center;
	}

	#msg {
		flex: 1;
		margin: 0 .5em;
	}
`);

customElements.define("wsp-space-selector", SpaceSelector);

class SpSelCreateSpace extends CreateSpace<SpaceSelector> {

	constructor() {
		super();
		this._visSrcPerms = this._enableSrcPerms; //Masque le btn si pas les droits.
	}

	async execute(ctx: SpaceSelector, ev?: Event): Promise<JSrcFields> {
		const sd = await super.execute(ctx, ev);
		if (sd) await ctx.spaceTree.selectSrcUri(sd.srcUri);
		return sd;
	}
}
